import React, { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from "react"
import { useDispatch, useSelector, useStore } from "react-redux"
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  split,
  Observable,
  Operation,
} from "@apollo/client"
import { createUploadLink } from "apollo-upload-client"
import { HttpLink } from "@apollo/client/link/http"
import { GraphQLWsLink } from "@apollo/client/link/subscriptions"
import { createClient } from "graphql-ws"
import { setContext } from "@apollo/client/link/context"
import { getMainDefinition } from "@apollo/client/utilities"
import { RetryLink } from "@apollo/client/link/retry"
import { onError } from "@apollo/client/link/error"
import * as Sentry from "@sentry/react"

import { config } from "Utils/config"
import { authIdentityFromToken } from "Utils/jwt"
import { userLogoutAction, setFirewallBlocked } from "Containers/Login/actions"
import { WebSocketStatusContext } from "Shared/context/WebSocketStatus.context"
import SplashScreen from "./components/SplashScreen"

interface IWrapper {
  children: ReactNode
}

// Code 4403 = graphql-ws Forbidden (server rejected auth)
const WS_FORBIDDEN_CODE = 4403
// Plafond du backoff de reconnexion : on ne veut ni marteler le serveur, ni attendre des heures.
const WS_MAX_RETRY_WAIT_MS = 15_000

export type WsStatus = "connected" | "disconnected" | "reconnecting"

/**
 * Une fermeture de socket est-elle ATTENDUE — c'est-à-dire provoquée par NOUS ?
 *
 * Le cas à neutraliser est `client.terminate()`, qu'on appelle à chaque changement de token
 * (login / logout / bascule de compte) pour forcer la ré-authentification du socket : le handler
 * `closed` le recevait comme n'importe quelle coupure, mettait le statut à "disconnected", et
 * `WebSocketLostModal` s'ouvrait ~5 s plus tard — en annonçant un serveur en ligne, puisque le
 * ping HTTP répondait.
 *
 * On l'identifie par le `name` du `TerminatedCloseEvent`, un événement **fabriqué localement** par
 * graphql-ws. Son code interne 4499 n'est jamais envoyé par un serveur, mais c'est le `name` qui
 * fait foi : il ne peut pas venir du réseau.
 *
 * ⚠️ **Aucun code de fermeture ne prouve qui a fermé.** Une première version élargissait cette
 * liste aux codes 1000 / 1001, sur l'idée que la fermeture paresseuse (`lazy: true`, dernier
 * abonnement terminé) ferme avec 1000. Mais un serveur peut envoyer 1000 lui aussi, et rien ne
 * les distingue côté client : une vraie coupure passait alors pour un arrêt propre, laissant les
 * consommateurs sur "connected". C'est inutile en plus d'être faux — graphql-ws gère déjà ce cas
 * mieux que nous : sur un 1000 il ne renonce à se reconnecter que si `locks === 0`, c'est-à-dire
 * s'il ne reste **aucun abonnement actif** (`shouldRetryConnectOrThrow`). S'il en reste un, il se
 * reconnecte, et le `connected` qui suit remet le statut au vert bien avant le seuil d'affichage
 * de la modale.
 */
export const isExpectedWsClose = (event: unknown): boolean =>
  (event as Error)?.name === "TerminatedCloseEvent"

/**
 * Miroir du compteur `locks` interne à graphql-ws : nombre de souscriptions ACTIVES.
 *
 * Pourquoi le tenir nous-mêmes : la lib ne l'expose pas, et sans lui le handler `closed` ne peut
 * pas distinguer les deux fermetures en code 1000 qu'il reçoit — celle d'un serveur qui coupe, et
 * la **fermeture paresseuse** que graphql-ws provoque de lui-même dès que le dernier abonnement
 * disparaît (`lazy: true`, `lazyCloseTimeout: 0` → `socket.close(1000, 'Normal Closure')`).
 *
 * La seconde n'est pas une coupure, et elle ne se répare pas : sur un 1000, la lib ne retente que
 * s'il reste un lock (`shouldRetryConnectOrThrow` → `return locks > 0`). À zéro lock, aucun
 * `connected` ne viendra donc jamais annuler un statut "disconnected" posé à tort. C'est ce qui
 * arrivait sur `/portal/:portalId/*`, monté `noLayout` — donc sans Header/Menu/Notifications, les
 * seuls abonnés permanents de l'application : en y entrant, le dernier abonnement mourait, et
 * `WebSocketLostModal` s'ouvrait ~6,5 s plus tard, bloquante, jusqu'à la sortie de l'écran.
 *
 * L'invariant que ce compteur permet d'écrire : **une fermeture ne compte comme coupure que si
 * quelqu'un attend encore du temps réel.** À zéro abonnement, il n'y a rien à annoncer et rien à
 * rétablir.
 */
export interface WsSubscriptionCounter {
  /** Un abonnement au moins est actif. */
  hasActive: () => boolean
  /** À appeler à la souscription. */
  acquire: () => void
  /** À appeler au désabonnement / à la complétion / sur erreur — idempotent par abonnement. */
  release: () => void
}

export const createWsSubscriptionCounter = (
  onLastRelease: () => void = () => undefined
): WsSubscriptionCounter => {
  let active = 0
  return {
    hasActive: () => active > 0,
    acquire: () => {
      active += 1
    },
    release: () => {
      if (active === 0) return
      active -= 1
      if (active === 0) onLastRelease()
    },
  }
}

/**
 * Lien qui tient le compteur ci-dessus à jour, monté DEVANT le lien WebSocket.
 *
 * ⚠️ L'ordre de la libération compte : on décrémente **avant** de propager le désabonnement en
 * aval, car c'est cette propagation qui fait relâcher son lock à graphql-ws, donc fermer le
 * socket. Au moment où l'événement `closed` arrive (`WebSocket.close()` ne déclenche jamais
 * `onclose` de façon synchrone), le compteur est déjà à zéro et le handler voit juste.
 */
export const createWsSubscriptionCountingLink = (counter: WsSubscriptionCounter): ApolloLink =>
  new ApolloLink(
    (operation, forward) =>
      new Observable(observer => {
        let released = false
        const release = () => {
          if (released) return
          released = true
          counter.release()
        }
        counter.acquire()
        const subscription = forward(operation).subscribe({
          next: value => observer.next(value),
          error: error => {
            release()
            observer.error(error)
          },
          complete: () => {
            release()
            observer.complete()
          },
        })
        return () => {
          release()
          subscription.unsubscribe()
        }
      })
  )

/**
 * Délai avant la prochaine tentative de reconnexion du socket.
 *
 * Backoff exponentiel **plafonné**, avec gigue. Le plafond n'est pas cosmétique : associé à
 * `retryAttempts: Infinity`, un backoff qui double sans fin reviendrait à abandonner au bout de
 * quelques minutes. Ici, une coupure longue est réessayée au pire toutes les 15 s, indéfiniment.
 * La gigue évite que tous les onglets ouverts ne se reconnectent à la même milliseconde.
 */
export const wsRetryDelayMs = (retries: number, random: () => number = Math.random): number => {
  const capped = Math.min(1000 * 2 ** Math.max(0, retries), WS_MAX_RETRY_WAIT_MS)
  return Math.round(capped * (0.5 + random() / 2))
}

/**
 * Handler `closed` du client graphql-ws. Exporté pour être testable : la distinction
 * « fermeture propre / vraie coupure » pilote une modale bloquante.
 */
export const createWsCloseHandler =
  (
    setStatus: (status: WsStatus) => void,
    onAuthFailure: () => void,
    hasActiveSubscriptions: () => boolean
  ) =>
  (event: unknown) => {
    // Un 4403 est un refus d'authentification PROUVÉ par le serveur : il vaut déconnexion même
    // sans abonnement actif — le socket ne peut de toute façon pas se rétablir avec ce jeton.
    if ((event as CloseEvent)?.code === WS_FORBIDDEN_CODE) {
      setStatus("disconnected")
      onAuthFailure()
      return
    }
    // Fermeture que nous avons provoquée : non-événement. On laisse le statut tel quel — la
    // prochaine souscription redialera et émettra `connected`.
    if (isExpectedWsClose(event)) return
    // Personne n'attend de temps réel : il n'y a rien à annoncer. C'est ici que passe la
    // fermeture paresseuse de graphql-ws (cf. `createWsSubscriptionCounter`), qu'aucun code de
    // fermeture ne permet de distinguer d'une coupure serveur.
    if (!hasActiveSubscriptions()) return
    // Tout le reste est traité comme une coupure, y compris un 1000 « propre » venu du serveur.
    // Si graphql-ws se reconnecte dans la foulée, le `connected` qui suit annule ce statut avant
    // même que la bannière ne l'applique (debounce de 1,5 s côté wrapper).
    setStatus("disconnected")
  }

// Création du client WebSocket GraphQL
const createGraphqlWsClient = (
  store: any,
  setStatus: (status: WsStatus) => void,
  onAuthFailure: () => void,
  counter: WsSubscriptionCounter
) =>
  createClient({
    url: `${config.APOLLO_WS_URL}/graphql`,
    // Read the token LIVE from the store on every (re)connect — same rationale as the HTTP
    // authLink below. Capturing it at creation meant a client built on the login screen (no
    // token) kept an empty token, so the post-login WS never authenticated until a full page
    // reload rebuilt it ("Connexion au serveur perdue" banner). Live read = works after login.
    connectionParams: () => {
      const token = store.getState()?.login?.jwt
      return { headers: { Authorization: token ? `Bearer ${token}` : "" } }
    },
    lazy: true,
    keepAlive: 30000,
    // Reconnexion SANS plafond. Avec 5 tentatives et un backoff exponentiel, le client
    // abandonnait au bout d'une trentaine de secondes : passé ce délai, le socket ne revenait
    // JAMAIS de lui-même, même serveur rétabli. La modale « connexion perdue » restait donc
    // ouverte indéfiniment et le rechargement de page était la seule issue. Un redémarrage de
    // back dure couramment plus de 30 s.
    retryAttempts: Infinity,
    retryWait: (retries: number) =>
      new Promise(resolve => setTimeout(resolve, wsRetryDelayMs(retries))),
    shouldRetry: (errOrCloseEvent: unknown) => {
      const code = (errOrCloseEvent as CloseEvent)?.code
      if (code === WS_FORBIDDEN_CODE) {
        onAuthFailure()
        return false
      }
      return true
    },
    on: {
      connected: () => setStatus("connected"),
      closed: createWsCloseHandler(setStatus, onAuthFailure, counter.hasActive),
      // Même invariant que `closed` : sans abonnement actif, un statut dégradé ne serait ni
      // pertinent (personne n'attend d'événement) ni réparable (la lib ne redialera pas
      // d'elle-même), et resterait affiché jusqu'au rechargement de la page.
      error: () => {
        if (counter.hasActive()) setStatus("reconnecting")
      },
    },
  })

// Lien HTTP — auth via Bearer header (Redux), pas de cookie
const createHttpLinkWithCsrf = () =>
  new HttpLink({ uri: `${config.APOLLO_SERVER_URL}/graphql` })

// Ajout des headers Authorization — lit le token FRAIS depuis le store à chaque
// requête, pour éviter une race au login : si on capturait accessToken dans la
// closure au moment de la création du link, la 1ère query déclenchée par le
// passage skip:true→false (ex: GET_BUSINESS_SETTINGS) partait avec l'ancien
// token vide avant que le useEffect du wrapper ait eu le temps de reconstruire
// le link. Lecture live = plus de race, et le link n'a plus besoin d'être
// recréé à chaque changement de token.
const createAuthLink = (store: any) =>
  setContext((_, { headers }) => {
    const token = store.getState()?.login?.jwt
    return { headers: { ...headers, Authorization: token ? `Bearer ${token}` : "" } }
  })

/**
 * Décide si une opération en échec peut être rejouée par le RetryLink.
 * Exportée pour être testable : la règle « on ne rejoue jamais une écriture » est une garantie
 * d'intégrité des données, pas un détail de configuration.
 */
export const shouldRetryOperation = (error: any, op: Operation): boolean => {
  // Une mutation n'est PAS idempotente : la rejouer sur une erreur réseau transitoire (requête
  // partie, réponse perdue) crée un doublon côté serveur — silencieusement, et jusqu'à 20 fois.
  // Seules les lectures sont sûres à rejouer.
  const definition = getMainDefinition(op.query)
  if (definition.kind === "OperationDefinition" && definition.operation === "mutation") return false

  // ⚠️ Les exclusions par NOM ci-dessous ne valent que pour des LECTURES. Une écriture est déjà
  // couverte par la règle de type juste au-dessus — la nommer en plus est un doublon, et un
  // doublon trompeur : il laisse croire que la politique repose sur une liste de noms à tenir
  // alors qu'elle repose sur le type d'opération, donc qu'elle protège toute mutation future
  // sans qu'on ait rien à faire. (Une exclusion nommée de `AddSampleForCSVExtactionData` a vécu
  // ici jusqu'à la suppression de cette mutation d'import, grc-back#580 ; elle était
  // inatteignable depuis l'ajout de la règle de type.)
  //
  // Lecture non gratuite : tant que l'enveloppe est en cours, `signSampleStatus` rappelle DocuSign
  // cache sauté côté back (~4 appels d'une API tierce quotée par tentative).
  if (op.operationName === "SignSampleStatus") return false
  const status = (error as any)?.statusCode ?? (error as any)?.networkError?.statusCode
  if (status === 401 || status === 403 || status === 404) return false
  // 5xx serveur : erreurs déterministes (un retry renverra le même 500). Les rejouer
  // jusqu'à 20× laissait l'UI bloquée plusieurs minutes en "loading" (ex: page blanche
  // post-login sur getMyPortals). On ne rejoue que les vraies erreurs réseau transitoires
  // (status indéfini = pas de réponse serveur).
  if (typeof status === "number" && status >= 500 && status < 600) return false
  return !!(error?.networkError ?? error)
}

/**
 * Journalisation + réactions transverses aux erreurs (logout sur 401, écran pare-feu sur 403).
 *
 * ⚠️ Ce lien **n'a pas le droit de rejouer l'opération**. Il termine volontairement sans rien
 * renvoyer : dans `onError`, retourner `forward(operation)` réémet la requête. Ce lien étant monté
 * EN AVAL du RetryLink (`ApolloLink.from([retryLink, errorLink, authLink, fileHttpLink])`), ce
 * rejeu partait droit vers le réseau, sans backoff et sans distinction lecture/écriture.
 *
 * Mesuré en local : une mutation en échec réseau produisait **deux** requêtes HTTP, émises à 1 ms
 * d'intervalle — donc deux écritures potentielles côté serveur, en silence. Le garde
 * `shouldRetryOperation` ci-dessus ne pouvait rien y faire, ce chemin ne passe pas par lui.
 *
 * Ne pas rejouer signifie que l'erreur remonte à l'appelant. C'est le comportement voulu : c'est
 * à l'écran de décider quoi afficher, et à l'utilisateur de relancer une écriture explicitement.
 * Les vraies erreurs réseau transitoires sur des LECTURES restent couvertes par le RetryLink.
 */
export const createErrorLink = (dispatch: any, store: any) =>
  onError(({ graphQLErrors, networkError }) => {
    graphQLErrors?.forEach(({ message }) => {
      console.error(`[GraphQL error]: ${message}`)
      Sentry.captureException(new Error(`[GraphQL error]: ${message}`))
    })
  
    if (networkError) {
      console.error(`[Network error]: ${networkError}`)
      const statusCode = networkError.toString().match(/Received status code (\d+)/)?.[1]
      if (statusCode === "401") {
        dispatch(userLogoutAction())
        return
      }
      Sentry.captureException(new Error(`[Network error]: ${networkError}`))
      // Ne déclencher logout que si on était authentifié — sinon on boucle login → 401 → login
      const currentToken = store.getState()?.login?.jwt
      if (statusCode === "401" && currentToken) dispatch(userLogoutAction())
      if (statusCode === "403") {
        const errorMsg = graphQLErrors?.[0]?.message || networkError.toString()
        if (errorMsg.includes("non autorisée")) {
          dispatch(setFirewallBlocked(errorMsg))
        }
      }
    }
  })

const FirewallBlockedScreen = ({ message }: { message: string }) => {
  const dispatch = useDispatch()
  const [retrying, setRetrying] = useState(false)

  const handleRetry = () => {
    setRetrying(true)
    dispatch(setFirewallBlocked(null))
    // Small delay to let Apollo re-fetch after Redux reset
    setTimeout(() => window.location.reload(), 100)
  }

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      fontFamily: "Roboto, sans-serif",
      background: "#f9fafb",
      padding: "40px",
      textAlign: "center",
    }}>
      <div style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "48px",
        maxWidth: "480px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        border: "1px solid #e5e7eb",
      }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>
          <i className="fas fa-shield-alt" style={{ color: "#dc2626" }} />
        </div>
        <h1 style={{ fontSize: "20px", fontWeight: 600, color: "#111827", margin: "0 0 12px" }}>
          Accès bloqué par le pare-feu
        </h1>
        <p style={{ fontSize: "14px", color: "#6b7280", margin: "0 0 24px", lineHeight: 1.6 }}>
          {message}
        </p>
        <p style={{ fontSize: "13px", color: "#9ca3af", margin: "0 0 24px" }}>
          Contactez votre administrateur si vous pensez que cette restriction est une erreur.
        </p>
        <button
          onClick={handleRetry}
          disabled={retrying}
          style={{
            padding: "10px 24px",
            borderRadius: "8px",
            border: "1px solid #d1d5db",
            background: "#fff",
            cursor: retrying ? "wait" : "pointer",
            fontSize: "14px",
            fontWeight: 500,
            color: "#374151",
          }}
        >
          {retrying ? "Chargement..." : "Réessayer"}
        </button>
      </div>
    </div>
  )
}

/**
 * Sur tout changement d'auth (connexion / déconnexion / changement de compte), deux choses à
 * lâcher : le socket WS **et le cache Apollo**.
 *
 * ## Le socket
 *
 * `connectionParams` n'est envoyé qu'au handshake initial : sans `terminate()`, le socket reste
 * authentifié en tant que l'utilisateur précédent après un logout → login-sur-un-autre-compte, et
 * l'écran affiche « Connexion au serveur perdue ». C'est un no-op quand rien n'est connecté
 * (connexion fraîche, avant la première souscription).
 *
 * ## Le cache — et pourquoi ce n'est pas cosmétique
 *
 * Le client Apollo est construit **une seule fois** (l'effet ci-dessus ne dépend que de
 * `[dispatch, store]`) ; un changement d'auth se contente de réinstaller le lien
 * (`setLink`). L'`InMemoryCache` **survit donc au changement d'utilisateur**, et rien d'autre
 * dans l'application n'appelle `resetStore`/`clearStore`.
 *
 * Le défaut passe inaperçu parce que le client est en `no-cache` par défaut — mais **~150
 * appels sortent explicitement de ce défaut**, et pas les moins sensibles :
 * `useIsAdmin`, `useIsTechAdmin`, `useAdminPermissions`, `useUserGroupIds`,
 * `GET_APP_USER_PROFIL`, `GET_BUSINESS_SETTINGS`, `GET_PAGES`… tous en `cache-first`. Sur un
 * enchaînement déconnexion → reconnexion **sur un autre compte dans le même onglet** — qui ne
 * recharge la page nulle part — le nouvel utilisateur peut donc se voir servir, **sans aucune
 * requête réseau**, le verdict d'administration, les groupes, les réglages et les titres de pages
 * du **tenant précédent**.
 *
 * L'impersonation, elle, n'est pas concernée : elle finit par `window.location.reload()`
 * (`Shared/hooks/useImpersonateUser`), donc le cache meurt avec la page. Quant à
 * `UserChangeDetector`, il n'est **pas monté** — `app/index.tsx` le commente, import compris,
 * depuis le 14/05/2026 ; et même monté il ne purgerait rien (il propose une modale que
 * l'utilisateur peut refuser).
 *
 * ## Deux opérations, pas une — le choix dépend de la transition
 *
 * | Transition | Opération | Pourquoi |
 * |---|---|---|
 * | connexion / bascule de compte | `resetStore()` | purge **et rejoue** les requêtes actives avec le nouveau jeton |
 * | déconnexion | `clearStore()` | rejouer avec un jeton mort produirait une volée de 401 dont le lien d'erreur redéclencherait le logout |
 *
 * `clearStore()` seul ne suffit pas sur une bascule, et c'est contre-intuitif : il ne relance
 * aucune requête et jette les watchers du cache, donc une requête `cache-first` **déjà montée**
 * (`BusinessThemeProvider`, à la racine) resterait affichée sur le tenant précédent. Le détail est
 * dans le corps de l'effet, au point de décision.
 *
 * ## Déclenché par l'IDENTITÉ, pas par la chaîne du jeton
 *
 * `authIdentityFromToken` rend `"<businessId>:<userId>"`. Une **réémission** de jeton pour le même
 * utilisateur (`GET /v2/user`, renouvellement de session) change la chaîne sans changer
 * l'identité : la traiter comme une bascule purgerait le cache et rejouerait toutes les requêtes
 * actives en pleine session, pour rien. Le repli est fail-closed — jeton illisible ou dépourvu de
 * ces claims ⇒ on purge quand même (cf. `Utils/jwt`).
 *
 * Corollaire assumé côté socket : un renouvellement de session ne coupe plus le WS. Il reste
 * authentifié avec le jeton de son handshake, ce que le serveur ne revérifie qu'à la connexion
 * (`ws-auth.middleware.onConnect`) ; toute reconnexion relit le jeton **frais** dans le store
 * (`connectionParams` ci-dessus). On y gagne la disparition d'une coupure — et de la bannière
 * « Connexion au serveur perdue » — à chaque renouvellement.
 */
export interface AuthPurgeableClient {
  clearStore: () => Promise<unknown>
  resetStore: () => Promise<unknown>
}

export const useDropStaleAuthState = (
  accessToken: string | null | undefined,
  getWsClient: () => { terminate: () => void } | null,
  getClient: () => AuthPurgeableClient | null
): void => {
  // L'IDENTITÉ, pas la chaîne du jeton — cf. `authIdentityFromToken`. `GET /v2/user` réémet un
  // jeton pour le même utilisateur : le traiter comme une bascule de compte purgerait le cache et
  // rejouerait toutes les requêtes actives en pleine session, pour rien.
  const identity = useMemo(() => authIdentityFromToken(accessToken), [accessToken])

  useEffect(() => {
    getWsClient()?.terminate()

    const client = getClient()
    if (!client) return

    // `catch` : la purge rejette les requêtes EN VOL (invariant Apollo 42). Ce n'est pas une erreur
    // à remonter — l'ancien contenu est parti dans tous les cas, et c'est ce qui compte ici.
    const settle = (p: Promise<unknown>) => p.catch(() => undefined)

    if (!identity) {
      // Déconnexion : purger sans rejouer. `resetStore` refetcherait les requêtes actives avec un
      // jeton mort — une volée de 401 dont le lien d'erreur redéclencherait le logout.
      settle(client.clearStore())
      return
    }

    // Connexion ou bascule de compte : purger **et rejouer les requêtes actives**.
    //
    // ⚠️ `clearStore()` seul ne suffit pas, et c'est contre-intuitif : dans Apollo 3.14
    // (`QueryManager.clearStore`), il annule les requêtes en vol, met les requêtes actives en
    // `networkStatus = loading` **sans en relancer aucune**, et jette les watchers du cache
    // (`discardWatches: true`). Une requête `cache-first` déjà montée resterait donc en chargement
    // perpétuel, affichant les données du tenant précédent.
    //
    // Ce n'est pas théorique : `BusinessThemeProvider` est monté à la RACINE (`app/index.tsx`),
    // donc son `GET_BUSINESS_SETTINGS` en `cache-first` ne démonte jamais — ni à la déconnexion, ni
    // à la reconnexion. Sans rejeu, le nouveau tenant garderait les couleurs et le nom
    // d'application du précédent jusqu'au prochain rechargement de page.
    //
    // `resetStore()` = `clearStore({ discardWatches: false })` + `reFetchObservableQueries()` :
    // les watchers survivent et chaque requête active repart avec le NOUVEAU jeton.
    settle(client.resetStore())
    // Les deux getters lisent des `ref`, volontairement hors du tableau de dépendances : ils sont
    // renseignés par un autre effet, et c'est l'identité — pas leur identité à eux — qui déclenche.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity])
}

const ApolloProviderWrapper = ({ children }: IWrapper) => {
  const accessToken = useSelector((state: any) => state.login.jwt)
  const firewallBlocked = useSelector((state: any) => state.login.firewallBlocked)
  const dispatch = useDispatch()
  const store = useStore()

  const clientRef = useRef<ApolloClient<any> | null>(null)
  const wsClientRef = useRef<ReturnType<typeof createGraphqlWsClient> | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [wsStatus, setWsStatus] = useState<WsStatus>(
    "connected"
  )

  // Debounced WS status: "connected" applies immediately (hides the banner at once), while a
  // problem state ("disconnected"/"reconnecting") is delayed ~1.5s. This absorbs the brief
  // transient of a NORMAL first-subscription handshake (arriving on a page that subscribes) so
  // the "Connexion au serveur perdue" banner no longer flashes; a genuine outage still shows it.
  const wsStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const setWsStatusDebounced = useCallback(
    (status: WsStatus) => {
      if (wsStatusTimerRef.current) {
        clearTimeout(wsStatusTimerRef.current)
        wsStatusTimerRef.current = null
      }
      if (status === "connected") {
        setWsStatus("connected")
        return
      }
      wsStatusTimerRef.current = setTimeout(() => setWsStatus(status), 1500)
    },
    []
  )

  useEffect(() => {
    // WS client created ONCE with a live-token connectionParams. lazy:true → it only dials on
    // the first subscription (i.e. after login, token available). No token-change rebuild and no
    // hot link-swap → no post-login "connection lost" flash / forced reload.
    const onAuthFailure = () => dispatch(userLogoutAction())
    // Le dernier abonnement qui s'en va remet le statut au vert : un écran sans temps réel n'a
    // pas d'état dégradé à afficher, et il n'en sortirait pas tout seul. Sans ça, une coupure
    // subie AVANT d'entrer sur le portail y laisserait la modale ouverte — même défaut, autre
    // chemin. Le prochain abonnement redialera et émettra un statut, lui, mesuré.
    const counter = createWsSubscriptionCounter(() => setWsStatusDebounced("connected"))
    const wsClient = createGraphqlWsClient(store, setWsStatusDebounced, onAuthFailure, counter)
    wsClientRef.current = wsClient
    const wsLink = createWsSubscriptionCountingLink(counter).concat(new GraphQLWsLink(wsClient))

    // Configuration des liens — authLink/errorLink lisent le token live (cf. createAuthLink)
    const authLink = createAuthLink(store)
    const retryLink = new RetryLink({
      attempts: { max: 20, retryIf: shouldRetryOperation },
      delay: { initial: 500, max: Infinity, jitter: true },
    })
    const errorLink = createErrorLink(dispatch, store)

    // Lien d'upload multipart avec auth + CSRF
    const uploadLink = authLink
      .concat(
        setContext((_, { headers }) => ({
          headers: { ...headers, "apollo-require-preflight": "true" },
        }))
      )
      .concat(createUploadLink({ uri: `${config.APOLLO_SERVER_URL}/graphql` }))

    const httpLink = createHttpLinkWithCsrf()

    // Split entre upload et HTTP classique (uniquement en client)
    const fileHttpLink =
      typeof window !== "undefined"
        ? ApolloLink.split(
            ({ variables }) =>
              Object.values(variables || {}).some(v => v instanceof File || v instanceof Blob),
            uploadLink,
            httpLink
          )
        : httpLink

    // Pipeline HTTP (retry → error → auth → upload/HTTP)
    const httpPipeline = ApolloLink.from([retryLink, errorLink, authLink, fileHttpLink])

    // Split entre WS et HTTP (only if WS link is available)
    const finalLink = wsLink
      ? split(
          ({ query }) => {
            const definition = getMainDefinition(query)
            return definition.kind === "OperationDefinition" && definition.operation === "subscription"
          },
          wsLink,
          httpPipeline
        )
      : httpPipeline

    if (!clientRef.current) {
      clientRef.current = new ApolloClient({
        link: finalLink,
        // 🚨 ApolloClient exige un cache, même avec fetchPolicy:"no-cache"
        cache: new InMemoryCache({
          typePolicies: {
            // Ces types UX sont des sous-objets "embedded" (pas d'ID), et peuvent être renvoyés
            // partiellement (ex: createForm dans une requête, infoTable dans une autre).
            // Sans keyFields/merge, Apollo peut lever un invariant lors du merge.
            uxReferentialType: { keyFields: false },
            uxTableComponentType: { keyFields: false },
            uxFormComponentType: { keyFields: false },
            uxMenuItemType: {
              fields: {
                folders: {
                  merge(_existing: any, incoming: any) {
                    return incoming
                  },
                },
              },
            },
            ReferentialType: {
              fields: {
                // Referentiel.ux est un objet "embedded" sans ID : on merge shallow pour éviter la perte
                // de champs lors de réponses partielles (ex: createForm seulement vs infoTable seulement).
                ux: {
                  merge(existing = {}, incoming = {}) {
                    return { ...existing, ...incoming }
                  },
                },
              },
            },
          },
        }),
        // ⚠️ AVANT DE RALLUMER LE CACHE : `getReferentialSamples` n'est PAS normalisé
        // (`ReferentialSampleType` a pour clé `idSample`, pas `id` ; `LinkType`/`BackLinkType`
        // ont `idRelation`). Or depuis grc-back#562, deux appelants du MÊME document envoient des
        // `relationIds` différents — l'ouverture de fiche envoie un périmètre, `useGetSamples`
        // (FlowChart, lancement de campagne) n'en envoie aucun. Cache allumé sans clés, les deux
        // réponses écriraient dans la même entrée et s'écraseraient : la fiche verrait les liens
        // de la grille, ou l'inverse, SANS erreur.
        // Garde : `Referentials/services/__tests__/apolloCacheNormalization.guard.test.ts`
        // (elle rougit si l'on retire ce `no-cache` sans avoir posé les `keyFields`).
        defaultOptions: {
          query: { fetchPolicy: "no-cache" },
          mutate: { fetchPolicy: "no-cache" },
          watchQuery: { fetchPolicy: "no-cache" },
        },
      })
      setIsReady(true)
    } else {
      clientRef.current.setLink(finalLink)
    }

    return () => {
      if (wsStatusTimerRef.current) clearTimeout(wsStatusTimerRef.current)
      wsClient?.dispose()
    }
  }, [dispatch, store])

  useDropStaleAuthState(
    accessToken,
    () => wsClientRef.current,
    () => clientRef.current
  )

  // Le client Apollo est construit dans un useEffect : au tout premier rendu React on n'est pas
  // encore prêt. Or ce rendu écrase déjà le splash statique de public/index.html — sans le même
  // écran ici, l'utilisateur voit un texte brut à la place du logo pendant ce laps de temps.
  if (!isReady || !clientRef.current) return <SplashScreen />

  if (firewallBlocked) {
    return <FirewallBlockedScreen message={firewallBlocked} />
  }

  return (
    <WebSocketStatusContext.Provider value={wsStatus}>
      <ApolloProvider client={clientRef.current}>{children}</ApolloProvider>
    </WebSocketStatusContext.Provider>
  )
}

export default ApolloProviderWrapper
